import { PortfolioChart } from '@/components/portal-modules/PortalChart';
import { BalancesSummaryReturn, ProfitLoss } from '@/types/sso';
import { getUniversalInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { useMemo } from 'react';
// Professional financial palette
const COLORS = [
    '#0ea5e9',
    '#6366f1',
    '#10b981',
    '#f59e0b',
    '#ec4899',
    '#8b5cf6',
    '#14b8a6',
    '#f97316',
    '#64748b',
    '#d946ef'
];

export interface Position {
    id: string;
    instrument: string;
    assetClass: 'Equity' | 'FX' | 'Fixed Income' | 'Commodity' | 'Crypto';
    instrumentCcy: string;
    marketValue: number; // in base currency (USD)
}

interface PortalPortfolioProps {
    balances?: BalancesSummaryReturn;
    netEquity?: {
        netEquity: number;
    };
    profitLoss?: {
        fx: ProfitLoss;
        cryptoCfd: ProfitLoss;
        indexCfd: ProfitLoss;
        futureCfd: ProfitLoss;
    };
    baseCurrency: string;
}

function PortalPortfolio({ balances, netEquity, profitLoss, baseCurrency }: PortalPortfolioProps) {
    const lang = useLanguage();

    const totalPositionsValue = useMemo(() => {
        if (profitLoss) {
            let total = 0;
            Object.keys(profitLoss).forEach((key) => {
                const pl = profitLoss[key as keyof typeof profitLoss];
                pl.data.forEach((item) => (total += item.marketValueInBaseCurrency));
            });
            return total;
        }
        return 1;
    }, [profitLoss]);

    const assetClassData = useMemo(() => {
        if (profitLoss) {
            const map = new Map<string, number>();

            Object.keys(profitLoss).forEach((key) => {
                const pl = profitLoss[key as keyof typeof profitLoss];
                let keyLabel = key;
                switch (key) {
                    case 'fx':
                        keyLabel = 'FX';
                        break;
                    case 'cryptoCfd':
                        keyLabel = 'Crypto CFD';
                        break;
                    case 'indexCfd':
                        keyLabel = 'Index CFD';
                        break;
                    case 'futureCfd':
                        keyLabel = 'Future CFD';
                        break;
                    case 'fx_option':
                        keyLabel = 'FX Option';
                        break;
                    case 'commodityCfd':
                        keyLabel = 'Commodity CFD';
                        break;
                    default:
                        keyLabel = key;
                }

                // map.set(keyLabel, pl.totalInBaseCurrency);
                pl.data.forEach((item) => {
                    const currentValue = map.get(keyLabel) || 0;
                    const itemValue = item.marketValueInBaseCurrency;
                    map.set(keyLabel, currentValue + itemValue);
                });
                // map.set(keyLabel, Math.abs(pl.totalInBaseCurrency));
            });

            const entries = Array.from(map.entries());

            const absTotal = entries.reduce((sum, [name, value]) => {
                const itemValue = Math.abs(value);
                return sum + itemValue;
            }, 0);

            const result = Array.from(map.entries()).map(([name, value], index) => ({
                name,
                value,
                absValue: Math.abs(value),
                percentage: Math.abs((value / absTotal) * 100),
                color: COLORS[index % COLORS.length]
            }));
            // .filter((item) => item.value > 0);

            return result.sort((a, b) => b.value - a.value);
        }
    }, [profitLoss]);

    const instrumentData = useMemo(() => {
        if (profitLoss) {
            const map = new Map<string, number>();

            Object.keys(profitLoss).forEach((key) => {
                const pl = profitLoss[key as keyof typeof profitLoss];
                pl.data.forEach((item) => {
                    let instrumentName = item.instrumentName;
                    const instrumentConfig = getUniversalInstrument(item.instrumentName);

                    if (instrumentConfig?.type === 'Future') {
                        instrumentName = instrumentConfig?.gcexSymbol;
                    }

                    map.set(instrumentName, item.marketValueInBaseCurrency);
                });
            });

            const entries = Array.from(map.entries());

            const absTotal = entries.reduce((sum, [name, value]) => {
                const itemValue = Math.abs(value);
                return sum + itemValue;
            }, 0);

            const result = Array.from(map.entries()).map(([name, value], index) => ({
                name,
                value,
                absValue: Math.abs(value),
                percentage: Math.abs((value / absTotal) * 100),
                color: COLORS[index % COLORS.length]
            }));
            // .filter((item) => item.value > 0);

            return result.sort((a, b) => b.value - a.value);
        }
    }, [profitLoss]);

    // const currencyData = useMemo(() => {
    //     if (profitLoss) {
    //         const map = new Map<string, number>();

    //         Object.keys(profitLoss).forEach((key) => {
    //             const pl = profitLoss[key as keyof typeof profitLoss];
    //             pl.data.forEach((item) => {
    //                 const currency = item.instrumentName.split('/')[1] || baseCurrency;
    //                 const currentValue = map.get(currency) || 0;
    //                 const itemValue =
    //                     item.subRows?.reduce((sum, sub) => sum + Math.abs(sub.marketValueInBaseCurrency), 0) ||
    //                     Math.abs(item.marketValueInBaseCurrency);
    //                 map.set(currency, currentValue + itemValue);
    //             });
    //         });

    //         const result = Array.from(map.entries()).map(([name, value], index) => ({
    //             name,
    //             value,
    //             absValue: Math.abs(value),
    //             percentage: (value / totalPositionsValue) * 100,
    //             color: COLORS[index % COLORS.length]
    //         }));
    //         // .filter((item) => item.value > 0);

    //         return result.sort((a, b) => b.value - a.value);
    //     }
    // }, [totalPositionsValue, profitLoss]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Asset Class Distribution */}
            {profitLoss && assetClassData && (
                <div className="h-full">
                    <PortfolioChart
                        title={lang.portal.assetClassAllocation}
                        data={assetClassData}
                        totalValue={totalPositionsValue}
                        baseCurrency={baseCurrency}
                    />
                </div>
            )}
            {/* Instrument Distribution */}
            {profitLoss && instrumentData && (
                <div className="h-full">
                    <PortfolioChart
                        title={lang.portal.instrumentExposure}
                        data={instrumentData}
                        totalValue={totalPositionsValue}
                        baseCurrency={baseCurrency}
                    />
                </div>
            )}

            {/* Currency Distribution */}
            {/* {profitLoss && currencyData && (
                <div className="h-full">
                    <PortfolioChart
                        title={lang.portal.currencyExposure}
                        data={currencyData}
                        totalValue={totalPositionsValue}
                        baseCurrency={baseCurrency}
                    />
                </div>
            )} */}
        </div>
    );
}

export default PortalPortfolio;
